import type { IOrder } from '../../../interfaces/Order';

type CreatedOrderLike = Partial<IOrder> & Record<string, any>;

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null;

const getRefId = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (!isRecord(value)) return null;

  const id = value._id ?? value.id;
  return typeof id === 'string' ? id : null;
};

const mergeRef = (currentValue: unknown, updatedValue: unknown) => {
  if (updatedValue === undefined) return currentValue;

  const currentId = getRefId(currentValue);
  const updatedId = getRefId(updatedValue);
  if (isRecord(currentValue) && typeof updatedValue === 'string' && currentId === updatedId) {
    return currentValue;
  }

  return updatedValue;
};

const preserveWhenMissing = (
  currentOrder: CreatedOrderLike | null | undefined,
  updatedOrder: CreatedOrderLike,
  key: string
) => (Object.prototype.hasOwnProperty.call(updatedOrder, key) ? updatedOrder[key] : currentOrder?.[key]);

export const mergeCreatedOrderUpdate = (
  currentOrder: CreatedOrderLike | null | undefined,
  updatedOrder: CreatedOrderLike
): CreatedOrderLike => {
  if (!currentOrder) return updatedOrder;

  return {
    ...currentOrder,
    ...updatedOrder,
    customer: mergeRef(currentOrder.customer, updatedOrder.customer),
    createdIn: mergeRef(currentOrder.createdIn, updatedOrder.createdIn),
    createdBy: mergeRef(currentOrder.createdBy, updatedOrder.createdBy),
    products: Array.isArray(updatedOrder.products) ? updatedOrder.products : currentOrder.products,
    buyAndGetProducts: Array.isArray(updatedOrder.buyAndGetProducts)
      ? updatedOrder.buyAndGetProducts
      : currentOrder.buyAndGetProducts,
    cashierBoxId: preserveWhenMissing(currentOrder, updatedOrder, 'cashierBoxId'),
    mpAttempt: preserveWhenMissing(currentOrder, updatedOrder, 'mpAttempt'),
    mpAttemptError: preserveWhenMissing(currentOrder, updatedOrder, 'mpAttemptError')
  };
};
